import { Box, Grid, Pagination, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import DataTable from "../components/DataTable";
import Meal from "../components/Meal";
import Subheader from "../components/Subheader";
import Api from "../openapi";
import { AdminPlanResponse } from "../openapi/client";
import {
  GridPaginationModel,
  GridCallbackDetails,
  GridSortModel,
  GridRenderCellParams,
  GridRowParams,
  MuiEvent,
} from "@mui/x-data-grid";
import { EditIcon } from "../assets/icons";
import MealCrud from "../components/MealCrud";
import { checkIf, generateRandom } from "../utils";
import useConfirm from "../hooks/useConfirm";
import { DeleteLabelIcon } from "../components/IconLabel";
import { useDispatch } from "react-redux";
import { setNotification } from "../store/notificationSlice";
import PlanCrud from "../components/PlanCrud";
import { useNavigate } from "react-router-dom";

export interface IEditPlan {
  show: boolean;
  plan?: AdminPlanResponse;
}

export default function Plan() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<AdminPlanResponse[]>([]);
  const [createShow, setCreateShow] = useState(false);
  const [editShow, setEditShow] = useState<IEditPlan>({ show: false, plan: undefined });
  const [loading, setLoading] = useState(false);
  const [retriggerReq, setRetriggerReq] = useState<number>();
  const [ConfirmDialog, confirmDelete] = useConfirm({
    titleIcon: <DeleteLabelIcon />,
    text: "You are about to permanently delete a plan from the list, and this action is irreversible. Are you sure you want to proceed?",
  });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const handlePaginationChange = (model: GridPaginationModel, details: GridCallbackDetails<any>) => {
    setPaginationModel(model);
  };

  const create = () => {
    setCreateShow(true);
  };

  const edit = (params: any) => {
    // console.log(params.row);
    setEditShow({ show: true, plan: params.row });
  };
  const deleteRec = async (params: any) => {
    const answer = await confirmDelete();
    if (!answer) return;

    try {
      const req = await Api.Plan.deletePlan(params.row.id);
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message: "Plan has been deleted successfully",
        })
      );
      setRetriggerReq(generateRandom());
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
  };

  const copy = (params: any) => {};

  const handleRowClick = (params: GridRowParams, event: any) => {
    // Check if clicking on action buttons
    let classList = event.target.classList;
    let prevent = classList.contains("MuiBox-root") || classList.value === "";

    // Check if clicking on a link wrapper
    const target = event.target as HTMLElement;
    const linkWrapper = target.closest('.row-link-wrapper');

    // If clicking on link wrapper with left-click only (no modifiers) and not on action buttons
    if (!prevent && linkWrapper && event.button === 0 && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      navigate(`/plan/${params.row.id}`);
    }
    // Otherwise let the browser handle it naturally (Ctrl+Click, middle-click, right-click)
  };

  const getRowNavigationUrl = (rowId: any) => {
    return `/plan/${rowId}`;
  };

  const handleClose = () => {
    setCreateShow(false);
    setEditShow({ show: false, plan: undefined });
  };
  const handleConfirm = () => {
    // popup returns refresh status
    // update the list
    setRetriggerReq(generateRandom());
  };

  const columns = useMemo(() => {
    return [
      {
        field: "name",
        minWidth: 300,
        flex: 1,
        headerName: "Plan Name",
        sortable: false,
        hideable: false,
      },
      {
        field: "active",
        flex: 0,
        headerName: "Status",
        sortable: false,
        hideable: false,
        valueGetter: (params: any) => (params.row.active ? "Active" : "Inactive"),
        renderCell: (params: GridRenderCellParams<any, any>) => (
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "normal",
              color: params.row.active ? "text.green" : "text.red",
            }}
          >
            {params.value}
          </Typography>
        ),
      },
    ];
  }, [plans]);

  useEffect(() => {
    const getPlans = async () => {
      setLoading(true);
      try {
        const res = await Api.Plan.listPlans();
        setPlans(res.data);
      } catch (error) {}
      setLoading(false);
    };
    getPlans();
  }, [retriggerReq]);

  return (
    <Box className="page-wrapper plan">
      <Subheader page="plans" />
      <Box p={6} className="page-container">
        <DataTable
          rows={plans}
          columns={columns}
          loading={loading}
          paginationMode="client"
          paginationModel={paginationModel}
          paginationChange={handlePaginationChange}
          rowClick={handleRowClick}
          getRowNavigationUrl={getRowNavigationUrl}
          create={create}
          edit={edit}
          deleteRec={deleteRec}
          copy={copy}
          options={{
            entityName: "plan",
            permission: { create: "PLAN_CREATE", edit: "PLAN_EDIT", delete: "PLAN_DELETE" },
          }}
        />
      </Box>
      <PlanCrud create open={createShow} title="New Plan" onClose={handleClose} onConfirm={handleConfirm} />
      <PlanCrud
        edit
        open={editShow.show}
        plan={editShow.plan}
        title="Edit Plan"
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
      <ConfirmDialog />
    </Box>
  );
}
